/**
 * Cohortes: comparar grupos de la flota por atributos de riesgo.
 *
 * Es la pantalla de la compañía de seguros. Recorre la flota que el usuario
 * puede ver, agrupa por las dimensiones que escoja —aseguradora, marca, línea,
 * rango de edad, género, ciudad, ocupación, uso— y publica la tasa por 100 km
 * de cada grupo.
 *
 * Tres cosas que el motor (`core/cohorts`) hace y esta pantalla se limita a
 * mostrar, porque son lo que separan un análisis actuarial de una tabla bonita:
 *
 *  1. **Tasa agregada, no promedio de tasas.** Σ episodios ÷ Σ km. Promediar
 *     las tasas individuales le daría el mismo peso a un vehículo de 20 km que
 *     a uno de 2.000, y el de 20 km es el más ruidoso.
 *  2. **Cohortes chicas no publican cifras.** Debajo del mínimo de vehículos o
 *     de kilómetros la celda sale suprimida, con el motivo. Protege a las
 *     personas y, sobre todo, impide leer ruido como señal.
 *  3. **La cobertura del atributo se muestra siempre.** Un cruce por ocupación
 *     sobre una flota donde el 90 % no tiene ocupación cargada no es un
 *     hallazgo sobre ocupaciones: es un hallazgo sobre datos faltantes.
 */

import { useCallback, useMemo, useState } from "react";
import {
  buildCohortReport,
  DEFAULT_THRESHOLDS,
  readCell,
  type CohortSubject,
} from "@core/cohorts/cohort-engine.ts";
import {
  COHORT_DIMENSIONS,
  DIMENSION_LABELS,
  UNKNOWN_ATTRIBUTE,
  type CohortDimension,
  type VehicleAttributes,
} from "@core/cohorts/attributes.ts";
import type { FleetVehicleRow } from "@core/fleet/fleet-comparison.ts";
import { requestFleetBatch } from "@web/lib/api-client.ts";
import { toBatchPage, useBatchWalk } from "@web/lib/batch-walk.ts";
import { Card, Notice } from "@web/components/primitives.tsx";

/** Columna de la base de atributos → dimensión de cohorte. */
const COLUMN_OF: Record<Exclude<CohortDimension, "rangoEdad">, string> = {
  aseguradora: "aseguradora",
  marca: "marca",
  linea: "linea",
  modeloAnio: "modelo_anio",
  ciudad: "ciudad",
  departamento: "departamento",
  genero: "genero",
  ocupacion: "ocupacion",
  usoServicio: "uso_servicio",
  tipoPersona: "tipo_persona",
};

const number = (value: number | null, digits = 2) =>
  value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: digits });

/** Convierte una fila de flota en un sujeto de cohorte, sin inventar nada. */
function toSubject(row: FleetVehicleRow): CohortSubject {
  const value = (column: string) => {
    const raw = row.attributes[column];
    return raw === null || raw === undefined || raw.trim() === ""
      ? { ...UNKNOWN_ATTRIBUTE }
      : { value: raw, system: "sync" as const, field: column, observedAt: null };
  };

  const attributes = {
    deviceId: row.deviceId,
    plate: row.plate,
    birthDate: value("fecha_nacimiento"),
    ...(Object.fromEntries(
      Object.entries(COLUMN_OF).map(([dimension, column]) => [dimension, value(column)]),
    ) as Omit<VehicleAttributes, "deviceId" | "plate" | "birthDate">),
  } as VehicleAttributes;

  return {
    deviceId: row.deviceId,
    plate: row.plate,
    attributes,
    analysis: {
      status: row.status,
      exposure: {
        eligibleDistanceKm: row.distanceKm,
        eligibleDurationHours: row.coveredHours,
      },
      dataQuality: { validPercent: row.validPercent ?? 0, received: 0 },
      technicalDriving: {
        consolidatedEpisodes: row.episodes.total,
        severityCounts: {
          low: 0,
          moderate: row.severity.moderate,
          high: row.severity.high,
          critical: row.severity.critical,
          inconclusive: 0,
        },
      },
    } as unknown as CohortSubject["analysis"],
    analysisSignature: row.analysisSignature,
    detectorsBlindedBySampling: row.detectorsBlindedBySampling,
    // Sin esta línea el motor de cohortes tiene el criterio nuevo escrito y no
    // lo usa nunca: cae siempre al de cadencia, que es el que dejaba entrar los
    // ceros mudos en la tasa. Un arreglo que no está cableado no es un arreglo.
    measurability: row.measurability,
  };
}

export function CohortsPanel({ period }: { period: { fromIso: string; toIso: string } | null }) {
  const [dimensions, setDimensions] = useState<CohortDimension[]>(["marca"]);
  const [minimumVehicles, setMinimumVehicles] = useState(DEFAULT_THRESHOLDS.minimumVehicles);

  const fetchPage = useCallback(
    async (input: { cursor: string | null; fromIso: string; toIso: string }) => {
      const response = await requestFleetBatch({
        fromIso: input.fromIso,
        toIso: input.toIso,
        cursor: input.cursor,
      });
      return toBatchPage<FleetVehicleRow>(response, response.rows);
    },
    [],
  );

  const walk = useBatchWalk<FleetVehicleRow>(fetchPage);

  const report = useMemo(() => {
    if (!walk.items.length || !period || !dimensions.length) return null;
    return buildCohortReport({
      subjects: walk.items.map(toSubject),
      dimensions,
      // La edad se calcula contra el FIN del período, no contra hoy: así el
      // mismo informe da el mismo rango cuando se vuelva a abrir en un año.
      periodEndDate: period.toIso.slice(0, 10),
      thresholds: { ...DEFAULT_THRESHOLDS, minimumVehicles },
    });
  }, [walk.items, dimensions, period, minimumVehicles]);

  const toggle = (dimension: CohortDimension) =>
    setDimensions((previous) =>
      previous.includes(dimension)
        ? previous.filter((item) => item !== dimension)
        : [...previous, dimension],
    );

  return (
    <div className="space-y-4">
      <Card
        title="Cohortes de riesgo"
        hint="Agrupa la flota por atributos y compara la tasa de episodios por 100 km de cada grupo."
      >
        <div className="flex flex-wrap gap-1.5">
          {COHORT_DIMENSIONS.map((dimension) => (
            <button
              key={dimension}
              type="button"
              onClick={() => toggle(dimension)}
              className={`rounded-full border px-2.5 py-1 text-xs ${
                dimensions.includes(dimension)
                  ? "border-brand-600 bg-brand-600 text-white"
                  : "border-zinc-300 text-zinc-600 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-400"
              }`}
            >
              {DIMENSION_LABELS[dimension]}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <button
            type="button"
            disabled={!period || walk.running}
            onClick={() => period && void walk.start(period)}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {walk.running ? "Recorriendo la flota…" : "Analizar cohortes"}
          </button>
          {walk.running ? (
            <button
              type="button"
              onClick={walk.stop}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
            >
              Detener
            </button>
          ) : null}

          <label className="flex flex-col gap-1 text-xs text-zinc-600 dark:text-zinc-400">
            Mínimo de vehículos por cohorte
            <select
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              value={minimumVehicles}
              onChange={(event) => setMinimumVehicles(Number(event.target.value))}
            >
              {[3, 5, 10, 20].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>

        {walk.batches > 0 ? (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
              <span>
                {walk.processed} de {walk.total} vehículos
              </span>
              <span>{walk.complete ? "Completo" : walk.running ? "En curso" : "Detenido"}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800">
              <div
                className="h-full bg-brand-600 transition-all"
                style={{ width: `${walk.total ? (walk.processed / walk.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        ) : null}
      </Card>

      {report?.unmeasuredWarning ? (
        <Notice tone="warning" title="Parte de la flota no se pudo medir">
          {report.unmeasuredWarning}
        </Notice>
      ) : null}

      {report ? (
        <Card
          title="Cobertura de los atributos"
          hint="Antes de leer una comparación, cuántos vehículos tienen de verdad el dato. Un cruce sobre una flota sin el atributo cargado habla de datos faltantes, no de riesgo."
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {report.coverage.map((item) => (
              <div key={item.dimension} className="text-xs">
                <div className="flex justify-between">
                  <span className="text-zinc-700 dark:text-zinc-300">{item.label}</span>
                  <span
                    className={`tabular-nums ${item.coveragePercent < 50 ? "text-amber-700 dark:text-amber-500" : "text-zinc-500"}`}
                  >
                    {number(item.coveragePercent, 0)} % · {item.distinctValues} valor(es)
                  </span>
                </div>
                <div className="mt-0.5 h-1.5 overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800">
                  <div
                    className={`h-full ${item.coveragePercent < 50 ? "bg-amber-500" : "bg-brand-600"}`}
                    style={{ width: `${item.coveragePercent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {report ? (
        <Card
          title={`Cohortes · ${report.cells.length}`}
          hint={`${report.subjects} vehículos analizados${report.withoutExposure ? `, ${report.withoutExposure} sin distancia elegible` : ""}. Tasa agregada: Σ episodios ÷ Σ km, no promedio de tasas.`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                  <th className="py-1.5 pr-2 font-medium">Cohorte</th>
                  {/*
                    «Midieron / total», porque son dos números distintos y la fila
                    publica cifras del primero. El encabezado decía «Vehículos» y
                    la columna imprimía el total, al lado de kilómetros y tasas
                    que describían solo a los que midieron.
                  */}
                  <th className="py-1.5 pr-2 text-right font-medium">Midieron / total</th>
                  <th className="py-1.5 pr-2 text-right font-medium">km</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Episodios</th>
                  <th className="py-1.5 pr-2 text-right font-medium">/100 km</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Mod.+ /100 km</th>
                  <th className="py-1.5 pr-2 text-right font-medium">Altos+crít.</th>
                </tr>
              </thead>
              <tbody>
                {report.cells.map((cell) => {
                  /*
                   * Qué números y qué palabras van en la fila lo decide el motor
                   * (`readCell`), con los umbrales del informe y no con el estado
                   * de este componente: los mínimos que se aplicaron son los que
                   * viajan en `report.thresholds`, y son los que hay que citar.
                   */
                  const reading = readCell(cell, report.thresholds);
                  return (
                    <tr key={cell.label} className="border-b border-zinc-100 last:border-0 dark:border-zinc-800/60">
                      <td className="py-1.5 pr-2">
                        <span className="text-zinc-900 dark:text-zinc-100">{cell.label}</span>
                        {reading.unmeasuredBadge ? (
                          <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-500">
                            {reading.unmeasuredBadge}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">
                        {reading.vehiclesLabel}
                      </td>
                      {cell.suppressed || !cell.metrics ? (
                        <td colSpan={5} className="py-1.5 pr-2 text-right text-xs text-zinc-500">
                          {reading.suppressionNote}
                        </td>
                      ) : (
                        <>
                          <td className="py-1.5 pr-2 text-right tabular-nums">
                            {number(cell.metrics.exposureKm, 0)}
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">{cell.metrics.episodes}</td>
                          <td className="py-1.5 pr-2 text-right font-medium tabular-nums">
                            {number(cell.metrics.episodesPer100Km, 1)}
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">
                            {number(cell.metrics.moderateOrHigherPer100Km, 1)}
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-500">
                            {cell.metrics.highOrCritical}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            Las cohortes suprimidas no se ocultan: se cuentan y se explican. Y las tasas se miden por
            100 km y no por hora, porque el equipo sigue reportando con el vehículo parqueado y una
            tasa por hora mediría estacionamiento.
          </p>
        </Card>
      ) : null}

      {walk.complete && !report ? (
        <Notice title="No hay cohortes que mostrar">
          Escoge al menos una dimensión, o carga los atributos de los vehículos: sin ellos toda la
          flota cae en la cohorte SIN_DATO.
        </Notice>
      ) : null}
    </div>
  );
}
